import React, { useEffect, useMemo, useState } from "react";
import $ from "jquery";
import { Award, Image as ImageIcon, MapPin, Package, Plus, Save, Trash2, Upload, Users } from "lucide-react";
import ApiURl from "../../controllers/Api";

const DEFAULT_STATS = [
  {
    icon: "package",
    value: "3,000+",
    title: "Products",
    text: "A wide product range created to serve different customer needs.",
  },
  {
    icon: "award",
    value: "40+",
    title: "Years of Experience",
    text: "Four decades of trust, craftsmanship and industry expertise.",
  },
  {
    icon: "mapPin",
    value: "19+",
    title: "States",
    text: "A strong and growing presence across multiple regions in India.",
  },
  {
    icon: "users",
    value: "150,000+",
    title: "Happy Customers",
    text: "Trusted by thousands of customers through quality and reliability.",
  },
];

const ICON_OPTIONS = [
  { value: "package", label: "Package", icon: Package },
  { value: "award", label: "Award", icon: Award },
  { value: "mapPin", label: "Location", icon: MapPin },
  { value: "users", label: "Users", icon: Users },
];

export default function AdminWhyKanthi() {
  const [form, setForm] = useState({
    section_title: "WHY KANTHI",
    section_subtitle: "Our legacy in numbers",
    main_title: "Built through decades of",
    highlighted_title: "excellence.",
    description:
      "Our journey is defined by product variety, industry experience, customer trust and a strong presence across India.",
  });
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [primaryFile, setPrimaryFile] = useState(null);
  const [secondaryFile, setSecondaryFile] = useState(null);
  const [primaryPreview, setPrimaryPreview] = useState("");
  const [secondaryPreview, setSecondaryPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const iconLabelMap = useMemo(
    () => Object.fromEntries(ICON_OPTIONS.map((item) => [item.value, item.label])),
    []
  );

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await $.getJSON(`${ApiURl}/getWhyKanthi.php`);
        if (!response?.success || !response?.data) return;

        const data = response.data;
        setForm((prev) => ({
          ...prev,
          section_title: data.section_title || prev.section_title,
          section_subtitle: data.section_subtitle || prev.section_subtitle,
          main_title: data.main_title || prev.main_title,
          highlighted_title: data.highlighted_title || prev.highlighted_title,
          description: data.description || prev.description,
        }));
        if (Array.isArray(data.stats) && data.stats.length > 0) {
          setStats(data.stats);
        }
        setPrimaryPreview(data.primary_image_url || "");
        setSecondaryPreview(data.secondary_image_url || "");
      } catch (error) {
        console.error("Error loading Why Kanthi settings:", error);
        setMessage("Could not load saved Why Kanthi settings.");
      }
    };

    fetchSettings();
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateStat = (index, field, value) => {
    setStats((prev) =>
      prev.map((stat, i) => (i === index ? { ...stat, [field]: value } : stat))
    );
  };

  const addStat = () => {
    setStats((prev) => [
      ...prev,
      { icon: "package", value: "0+", title: "New Stat", text: "Stat description" },
    ]);
  };

  const removeStat = (index) => {
    setStats((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageChange = (setter, previewSetter) => (event) => {
    const file = event.target.files?.[0] || null;
    setter(file);
    previewSetter(file ? URL.createObjectURL(file) : "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    payload.append("stats_json", JSON.stringify(stats));
    if (primaryFile) payload.append("primary_image", primaryFile);
    if (secondaryFile) payload.append("secondary_image", secondaryFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}/updateWhyKanthi.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        setMessage("Why Kanthi section updated successfully.");
        setPrimaryFile(null);
        setSecondaryFile(null);
      } else {
        setMessage(response?.message || "Failed to update Why Kanthi section.");
      }
    } catch (error) {
      console.error("Error saving Why Kanthi settings:", error);
      setMessage("Server error while saving Why Kanthi section.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-why-kanthi-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <div className="mx-auto mb-6 max-w-[1450px] rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:p-6">
        <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">Why Kanthi Section</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-[#6f5e57]">
          Edit the homepage heading, supporting copy, showcase images and trust statistics.
        </p>
      </div>

      {message && (
        <div className="mx-auto mb-6 max-w-[1450px] rounded-2xl border border-[#d4af37]/25 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19] shadow-[0_10px_25px_rgba(43,0,0,0.05)]">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mx-auto max-w-[1450px] space-y-7 rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-7">
        <div className="grid gap-4 rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6 md:grid-cols-2">
          <TextInput label="Section Heading" value={form.section_title} onChange={(value) => updateForm("section_title", value)} />
          <TextInput label="Section Subtitle" value={form.section_subtitle} onChange={(value) => updateForm("section_subtitle", value)} />
          <TextInput label="Main Title" value={form.main_title} onChange={(value) => updateForm("main_title", value)} />
          <TextInput label="Highlighted Title" value={form.highlighted_title} onChange={(value) => updateForm("highlighted_title", value)} />
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-[#4a352f]">Description</span>
          <textarea
            value={form.description}
            onChange={(event) => updateForm("description", event.target.value)}
            rows={4}
            className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
          />
        </label>

        <div className="grid gap-4 rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6 md:grid-cols-2">
          <ImageField
            label="Primary Image"
            preview={primaryPreview}
            onChange={handleImageChange(setPrimaryFile, setPrimaryPreview)}
          />
          <ImageField
            label="Secondary Image"
            preview={secondaryPreview}
            onChange={handleImageChange(setSecondaryFile, setSecondaryPreview)}
          />
        </div>

        <div className="overflow-hidden rounded-[22px] border border-[#d8c6b7] bg-white shadow-[0_16px_42px_rgba(43,0,0,0.06)]">
          <div className="flex items-center justify-between border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-4 py-4 sm:px-5">
            <h2 className="font-serif text-2xl font-medium text-[#2B0000]">Stats</h2>
            <button
              type="button"
              onClick={addStat}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
            >
              <Plus className="h-4 w-4" />
              Add Stat
            </button>
          </div>

          <div className="divide-y divide-[#eadfd5]">
            {stats.map((stat, index) => (
              <div key={index} className="grid gap-4 p-4 transition hover:bg-[#fff5ea] sm:p-5 lg:grid-cols-[150px_140px_1fr_1.2fr_auto]">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-[#4a352f]">Icon</span>
                  <select
                    value={stat.icon || "package"}
                    onChange={(event) => updateStat(index, "icon", event.target.value)}
                    className="w-full rounded-xl border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  >
                    {ICON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <TextInput label="Value" value={stat.value} onChange={(value) => updateStat(index, "value", value)} />
                <TextInput label="Title" value={stat.title} onChange={(value) => updateStat(index, "title", value)} />
                <TextInput label="Text" value={stat.text} onChange={(value) => updateStat(index, "text", value)} />

                <div className="flex items-end justify-between gap-2">
                  <span className="mb-2 text-xs font-medium text-[#7d6c64]">{iconLabelMap[stat.icon] || "Package"}</span>
                  <button
                    type="button"
                    onClick={() => removeStat(index)}
                    className="mb-0.5 rounded-lg border border-red-200 bg-[#fff4f1] p-2 text-red-600 transition hover:bg-red-600 hover:text-white"
                    aria-label="Remove stat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] shadow-[0_12px_28px_rgba(43,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:bg-[#b7a69b] disabled:text-white"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving..." : "Save Why Kanthi"}
        </button>
      </form>

      <style>{`
        .admin-why-kanthi-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .admin-why-kanthi-luxury input,
        .admin-why-kanthi-luxury select,
        .admin-why-kanthi-luxury textarea,
        .admin-why-kanthi-luxury button {
          font: inherit;
        }

        .admin-why-kanthi-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .admin-why-kanthi-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .admin-why-kanthi-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .admin-why-kanthi-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .admin-why-kanthi-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .admin-why-kanthi-luxury {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
}

function TextInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}

function ImageField({ label, preview, onChange }) {
  return (
    <div className="rounded-[20px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_12px_32px_rgba(43,0,0,0.05)]">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#2B0000]">
        <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
        {label}
      </div>
      <div className="mb-4 h-56 overflow-hidden rounded-[18px] border border-[#d8c6b7] bg-[#fbf3eb] shadow-inner">
        {preview ? (
          <img src={preview} alt={label} className="h-full w-full bg-[#fffaf5] object-contain" />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-[#9b877e]">
            No image selected
          </div>
        )}
      </div>
      <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:border-[#c59a4a] hover:bg-[#fff7ed]">
        <Upload className="h-4 w-4" />
        Upload Image
        <input type="file" accept="image/*" onChange={onChange} className="hidden" />
      </label>
    </div>
  );
}